var Promise = require('bluebird');
var m = require('mithril');
var prop = require('mithril/stream');
var moment = require('moment-timezone');
var dragscroll = require('../libs/dragscroll');
var header = require('../layout/components/header');
var heading = require('../components/heading');
var loaders = require('../layout/components/loaders');
var alerts = require('../layout/components/alerts');
var paginationWithSearch = require('./components/paginationWithSearch');
var suppressionDurationModal = require('./components/suppressionDurationModal');

var dateFormat = 'MM/DD/YYYY hh:mm:ss a';
var AUDIT_RESOURCE_PAGE_SIZE = 20;

function resourceBreadcrumbLabel (resourceRecord) {
  if (!resourceRecord) {
    return '';
  }
  if (Array.isArray(resourceRecord.breadcrumbs)) {
    return resourceRecord.breadcrumbs.map(function (e) { return e.name; }).join(' > ');
  }
  return resourceRecord.breadcrumbs || '';
}

function normalizeSuppressionValue (value) {
  if (value === null || typeof value === 'undefined') {
    return 0;
  }
  if (typeof value === 'string') {
    var lower = value.toLowerCase();
    if (lower === 'true') return 1;
    if (lower === 'false') return 0;
    var numFromString = Number(value);
    if (!Number.isNaN(numFromString)) {
      return numFromString === 0 ? 0 : 1;
    }
    return 0;
  }
  if (value === true) return 1;
  if (value === false) return 0;
  var num = Number(value);
  if (Number.isNaN(num)) {
    return 0;
  }
  return num === 0 ? 0 : 1;
}

function normalizeMuteExpiration (value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (value instanceof Date && typeof value.getTime === 'function') {
    return value.getTime();
  }
  if (typeof value === 'string') {
    var trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (/^\d+$/.test(trimmed)) {
      var asNumber = Number(trimmed);
      return Number.isFinite(asNumber) ? asNumber : null;
    }
    var parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    var fallback = Number(trimmed);
    return Number.isFinite(fallback) ? fallback : null;
  }
  return null;
}

function applyNormalizedSuppression (record) {
  if (!record) return;
  record.suppressAuditNotifications = normalizeSuppressionValue(record.suppressAuditNotifications);
  record.disableAdminAuditNotifications = normalizeSuppressionValue(record.disableAdminAuditNotifications);
  record.disableReceiverAuditNotifications = normalizeSuppressionValue(record.disableReceiverAuditNotifications);
  record.suppressAuditNotificationsExpiresAt = normalizeMuteExpiration(record.suppressAuditNotificationsExpiresAt);
}

function suppressionExpirationMeta (resourceRecord) {
  if (!resourceRecord) {
    return {label: '', title: '', indefinite: false, expired: false};
  }
  var ts = normalizeMuteExpiration(resourceRecord.suppressAuditNotificationsExpiresAt);
  if (!ts) {
    return {label: 'Indefinite', title: 'Indefinite', indefinite: true, expired: false};
  }
  var formatted = moment(ts).format('MM-DD-YYYY h:mm a');
  var isExpired = ts <= Date.now();
  return {
    label: formatted,
    title: formatted,
    indefinite: false,
    expired: isExpired
  };
}

function formatAuditTimestamp (value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return '';
  }
  var timestamp = moment(value);
  return timestamp.isValid() ? timestamp.format(dateFormat) : '';
}

function normalizeAuditResourceId (record) {
  if (!record) return null;
  if (typeof record === 'string') return record;
  if (record['@rid']) return record['@rid'].toString();
  return null;
}

function auditResourceIds (records) {
  return (Array.isArray(records) ? records : [])
    .map(normalizeAuditResourceId)
    .filter(function (rid) { return !!rid; });
}

function auditResourceSortParams (ascending) {
  return {
    sort: 'lastSuccess',
    direction: ascending ? 'asc' : 'desc'
  };
}

function suppressionToggleButton (resource, suppressed, saving, clickHandler) {
  var baseLabel = saving ? 'Saving…' : (suppressed ? 'Muted' : 'Mute');
  var meta = suppressionExpirationMeta(resource);
  var labelText = baseLabel;
  if (suppressed && !saving) {
    if (meta.indefinite) {
      labelText = 'Muted indefinitely';
    } else if (meta.expired) {
      labelText = 'Muted (expired ' + meta.label + ')';
    } else if (meta.label) {
      labelText = 'Until ' + meta.label;
    }
  }
  var baseClass = 'w-100 flex items-center justify-center pv2 pointer ba bw1';
  var colorClass = suppressed ? 'bg-gold near-black b--gold' : 'bg-transparent near-white b--gray';
  var opacityClass = saving ? ' o-60' : '';
  return m('button', {
    type: 'button',
    class: baseClass + ' ' + colorClass + opacityClass,
    disabled: saving,
    onclick: clickHandler,
    title: suppressed
      ? (meta.indefinite ? 'Muted indefinitely' : 'Muted until ' + (meta.label || 'unknown time'))
      : 'Mute audit notifications for this resource'
  }, (function () {
    if (saving) {
      return m('span.near-white', 'Saving…');
    }
    if (suppressed) {
      return m('span.tc.nowrap.near-black', labelText);
    }
    return m('i.ion-volume-mute.f3.near-white');
  })());
}

function auditResourceTableHead (ctrl, loading) {
  return m('thead', [
    m('tr', [
      m('th.ph0.tl', loading ? {} : { onclick: ctrl.sortResourcesBySuccess }, [
        'Last Success',
        ctrl.successSortMode() ? m('i.ion-ios-arrow-down.ml2') : m('i.ion-ios-arrow-up.ml2')
      ]),
      m('th.ph0.tl', 'Mute'),
      m('th.ph0.tl', 'Name'),
      m('th.ph0.tl', 'Alarm Monitoring'),
      m('th.ph0.tl', [
        'Offline For'
      ])
    ])
  ]);
}

function auditSkeletonBar (width) {
  return m('div.f4.br2.bg-dark-gray.o-20', {
    style: { height: '1em', width: width + '%' }
  });
}

function auditSkeletonCell (width, innerStyle) {
  return m('td.tl.bb.bl-0.pr2.skeleton', [
    m('div', {style: innerStyle || {}}, auditSkeletonBar(width))
  ]);
}

function auditSkeletonRows (rows) {
  var widths = [
    [60, {width: '12em'}],
    [64],
    [70],
    [28],
    [55, {width: '12em'}]
  ];
  var bodyRows = [];
  for (var index = 0; index < rows; index += 1) {
    bodyRows.push(m('tr', widths.map(function (cell, cellIndex) {
      var width = cell[0] - ((index + cellIndex) % 3) * 8;
      return auditSkeletonCell(width, cell[1]);
    })));
  }
  return bodyRows;
}

function auditResourcesTableShell (ctrl, bodyRows, loading) {
  return m('div.dragscroll-container.audit-table-scroll', [
    m('div.dragscroll', {oncreate: dragscroll}, [
      m('table.w-100.f4.mb0', [
        auditResourceTableHead(ctrl, loading),
        m('tbody', bodyRows)
      ])
    ])
  ]);
}

function auditResourcesSkeletonTable (ctrl, rows) {
  return auditResourcesTableShell(ctrl, auditSkeletonRows(rows || AUDIT_RESOURCE_PAGE_SIZE), true);
}

function auditEmptyRows (rows) {
  var bodyRows = [];
  for (var index = 0; index < rows; index += 1) {
    bodyRows.push(m('tr', {key: 'audit-empty-' + index}, [
      m('td.tl', m('div', {style: {width: '12em', visibility: 'hidden'}}, 'No Data')),
      m('td.tl', '\u00a0'),
      m('td.tl', '\u00a0'),
      m('td.tl', '\u00a0'),
      m('td.tl', m('div', {style: {width: '12em'}}, '\u00a0'))
    ]));
  }
  return bodyRows;
}

function auditResourcesTable (ctrl) {
  var resources = ctrl.resources();
  var bodyRows = resources.map(function (r) {
    return m('tr', {key: 'audit-resource-' + normalizeAuditResourceId(r)}, [
      m('td.tl', m('div', {style: {width: '12em'}}, formatAuditTimestamp(r.updated))),
      m('td.tl',
        suppressionToggleButton(r, ctrl.isSuppressed(r), r._savingSuppression, function () { ctrl.toggleNotificationSuppression(r); })
      ),
      m('td.tl',
        m('a.red', {
          href: '/#/' + (r['@id'].split('/').slice(3).join('/').toLowerCase())
        }, resourceBreadcrumbLabel(r))
      ),
      m('td.tl', Boolean(r.children.filter(p => Boolean(Number(p.alarmEnabled))).length) ? '✅' : ''),
      m('td.tl', m('div', {style: {width: '12em'}}, r.updated ? moment.duration(Date.now() - r.updated).humanize() : 'Forever')),
    ])
  });
  var emptyRowCount = Math.max(0, AUDIT_RESOURCE_PAGE_SIZE - resources.length);
  return auditResourcesTableShell(ctrl, bodyRows.concat(auditEmptyRows(emptyRowCount)), false);
}

var page = {};

page.oninit = function () {
  var ctrl = this;
  document.title = 'nimrod | Audit';
  header.vm.activeTab('Audit');
  ctrl.points = prop([]);
  ctrl.pointsToShow = [];
  ctrl.resources = prop([]);
  ctrl.resourceData = prop([]);
  ctrl.filterByTimeOffline = prop(false);
  ctrl.currentSort = prop('success');
  ctrl.successSortMode = prop(true);
  ctrl.newNode = prop(false);
  ctrl.filteredResourceCount = prop(0);
  ctrl.suppressedResourceIds = prop([]);
  ctrl.suppressedResources = prop([]);
  ctrl.loadingResourceIds = prop(true);
  ctrl.loadingResources = prop(false);
  ctrl.showResourceSkeleton = prop(false);
  ctrl.loadingSuppressed = prop(true);
  ctrl.suppressedSectionOpen = prop(false);
  ctrl.currentResourcePageKey = null;
  ctrl.resourcePageRequestId = 0;
  ctrl.resourceSkeletonTimer = null;

  ctrl.clearResourceSkeletonTimer = function () {
    if (ctrl.resourceSkeletonTimer) {
      clearTimeout(ctrl.resourceSkeletonTimer);
      ctrl.resourceSkeletonTimer = null;
    }
  };

  ctrl.scheduleResourceSkeleton = function (delay) {
    ctrl.clearResourceSkeletonTimer();
    ctrl.showResourceSkeleton(false);
    ctrl.resourceSkeletonTimer = setTimeout(function () {
      ctrl.resourceSkeletonTimer = null;
      if (ctrl.loadingResources()) {
        ctrl.showResourceSkeleton(true);
        m.redraw();
      }
    }, delay);
  };

  var applySuppressionSnapshot = function (rid, fields) {
    if (!rid) return;
    var ridKey = rid.toString ? rid.toString() : String(rid);
    var applyFields = function (record) {
      if (!record || !record['@rid'] || record['@rid'].toString() !== ridKey) return;
      if (typeof fields.suppressAuditNotifications !== 'undefined') {
        record.suppressAuditNotifications = normalizeSuppressionValue(fields.suppressAuditNotifications);
      }
      if (typeof fields.disableAdminAuditNotifications !== 'undefined') {
        record.disableAdminAuditNotifications = normalizeSuppressionValue(fields.disableAdminAuditNotifications);
      }
      if (typeof fields.disableReceiverAuditNotifications !== 'undefined') {
        record.disableReceiverAuditNotifications = normalizeSuppressionValue(fields.disableReceiverAuditNotifications);
      }
      if (typeof fields.suppressAuditNotificationsExpiresAt !== 'undefined') {
        record.suppressAuditNotificationsExpiresAt = normalizeMuteExpiration(fields.suppressAuditNotificationsExpiresAt);
      }
    };

    var resourceList = ctrl.resources();
    if (Array.isArray(resourceList)) {
      resourceList.forEach(applyFields);
    }
    var suppressedList = ctrl.suppressedResources();
    if (Array.isArray(suppressedList)) {
      suppressedList.forEach(applyFields);
    }
  };

  var sortResourcesByName = function (resources) {
    return (Array.isArray(resources) ? resources.slice() : []).sort(function (a, b) {
      var aName = (resourceBreadcrumbLabel(a) || '').toLowerCase();
      var bName = (resourceBreadcrumbLabel(b) || '').toLowerCase();
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      var aRid = (a && a['@rid']) ? a['@rid'].toString() : '';
      var bRid = (b && b['@rid']) ? b['@rid'].toString() : '';
      return aRid.localeCompare(bRid);
    });
  };

  ctrl.applyResourceSort = function () {
    var resources = ctrl.resources();
    if (!Array.isArray(resources)) return;
    if (ctrl.currentSort() === 'success') {
      if (ctrl.successSortMode()) {
        resources.sort((a,b) => (a.updated || 0) - (b.updated || 0));
      } else {
        resources.sort((a,b) => (b.updated || 0) - (a.updated || 0));
      }
    }
  };

  ctrl.fetchSuppressedResources = function () {
    ctrl.loadingSuppressed(true);
    return loaders.requestWithLoader({
      url: '/api/v1/audit/suppressed',
      method: 'GET',
      loader: 'background'
    })
    .then(function (result) {
      var ids = Array.isArray(result) ? result : [];
      ctrl.suppressedResourceIds(ids);
      if (!ids.length) {
        ctrl.suppressedResources([]);
        return null;
      }
      return loaders.requestWithLoader({
        url: '/api/v1/audit/resources',
        method: 'POST',
        body: ids,
        loader: 'background'
      })
      .then(function (resources) {
        var list = sortResourcesByName(resources || []);
        list.forEach(applyNormalizedSuppression);
        ctrl.suppressedResources(list);
        if (Array.isArray(list)) {
          list.forEach(function (record) {
            if (!record || !record['@rid']) return;
            applySuppressionSnapshot(record['@rid'], {
              suppressAuditNotifications: normalizeSuppressionValue(record.suppressAuditNotifications),
              disableAdminAuditNotifications: normalizeSuppressionValue(record.disableAdminAuditNotifications),
              disableReceiverAuditNotifications: normalizeSuppressionValue(record.disableReceiverAuditNotifications),
              suppressAuditNotificationsExpiresAt: normalizeMuteExpiration(record.suppressAuditNotificationsExpiresAt)
            });
          });
        }
      });
    })
    .catch(function (error) {
      var message = (error && error.message) || (error && error.error) || 'Failed to load muted resources';
      alerts.vm.add({detail: message});
    })
    .then(function () {
      ctrl.loadingSuppressed(false);
      m.redraw();
    });
  };

  ctrl.isSuppressed = function (resource) {
    if (!resource) return false;
    if (normalizeSuppressionValue(resource.suppressAuditNotifications) === 1) {
      return true;
    }
    var rid = resource['@rid'] ? resource['@rid'].toString() : null;
    var ids = ctrl.suppressedResourceIds();
    if (rid && Array.isArray(ids)) {
      return ids.indexOf(rid) !== -1;
    }
    return false;
  };

  ctrl.sortResourcesBySuccess = function () {
    ctrl.currentSort('success');
    ctrl.successSortMode(!ctrl.successSortMode());
    return ctrl.loadAuditResourceIds();
  }

  ctrl.loadResourcePage = function () {
    var pageIds = auditResourceIds(ctrl.pointsToShow);
    var pageKey = pageIds.join('|');
    if (pageKey === ctrl.currentResourcePageKey) {
      return Promise.resolve();
    }

    ctrl.currentResourcePageKey = pageKey;
    ctrl.loadingResources(true);
    ctrl.scheduleResourceSkeleton(ctrl.resources().length ? 150 : 0);

    if (!pageIds.length) {
      ctrl.resourcePageRequestId++;
      ctrl.clearResourceSkeletonTimer();
      ctrl.showResourceSkeleton(false);
      ctrl.resourceData([]);
      ctrl.resources([]);
      ctrl.loadingResources(false);
      m.redraw();
      return Promise.resolve([]);
    }

    var requestId = ++ctrl.resourcePageRequestId;
    return loaders.requestWithLoader({
      url: "/api/v1/audit/resources",
      method: "POST",
      body: pageIds,
      loader: 'background'
    })
    .then(function (result) {
      if (requestId !== ctrl.resourcePageRequestId) return;
      var resources = Array.isArray(result) ? result : [];
      resources.forEach(resourceRecord => { if (resourceRecord.lastOfflineTimestamp) resourceRecord.updated = resourceRecord.lastOfflineTimestamp })
      resources.forEach(applyNormalizedSuppression);
      ctrl.resourceData(resources);
      ctrl.resources(resources);
      ctrl.applyResourceSort();
      var suppressedExisting = ctrl.suppressedResources();
      if (Array.isArray(suppressedExisting) && suppressedExisting.length) {
        suppressedExisting.forEach(function (record) {
          if (!record || !record['@rid']) return;
          applySuppressionSnapshot(record['@rid'], {
            suppressAuditNotifications: record.suppressAuditNotifications,
            disableAdminAuditNotifications: record.disableAdminAuditNotifications,
            disableReceiverAuditNotifications: record.disableReceiverAuditNotifications,
            suppressAuditNotificationsExpiresAt: record.suppressAuditNotificationsExpiresAt
          });
        });
      }
    })
    .catch(function (error) {
      if (requestId !== ctrl.resourcePageRequestId) return;
      var message = (error && error.message) || (error && error.error) || 'Failed to load audit resources';
      alerts.vm.add({detail: message});
    })
    .then(function () {
      if (requestId === ctrl.resourcePageRequestId) {
        ctrl.clearResourceSkeletonTimer();
        ctrl.showResourceSkeleton(false);
        ctrl.loadingResources(false);
        m.redraw();
      }
    });
  };

  ctrl.loadAuditResourceIds = function () {
    ctrl.loadingResourceIds(true);
    return loaders.requestWithLoader({
      url: "/api/v1/audit/",
      method: "GET",
      params: auditResourceSortParams(ctrl.successSortMode())
    })
    .then(function (result) {
      var ids = auditResourceIds(result);
      ctrl.points(ids);
      ctrl.pointsToShow.splice(0);
      ctrl.pointsToShow.push(...ids.slice(0, AUDIT_RESOURCE_PAGE_SIZE));
      ctrl.filteredResourceCount(ids.length);
      ctrl.currentResourcePageKey = null;
      ctrl.newNode(true);
      return ctrl.loadResourcePage();
    })
    .catch(function (error) {
      var message = (error && error.message) || (error && error.error) || 'Failed to load audit resources';
      alerts.vm.add({detail: message});
    })
    .then(function () {
      ctrl.loadingResourceIds(false);
      m.redraw();
    });
  };

  ctrl.updateSuppression = function (resource, nextValue, expiresAt) {
    if (!resource || !resource['@rid']) return;
    resource._savingSuppression = true;
    var body = {
      suppressAuditNotifications: nextValue,
      disableAdminAuditNotifications: nextValue,
      disableReceiverAuditNotifications: nextValue,
      suppressAuditNotificationsExpiresAt: expiresAt || null
    };
    return loaders.requestWithLoader({
      url: '/api/v1/resource/' + resource['@rid'].toString().slice(1),
      method: 'PUT',
      body: body,
      loader: 'background'
    })
    .then(function () {
      resource.suppressAuditNotifications = nextValue;
      resource.disableAdminAuditNotifications = nextValue;
      resource.disableReceiverAuditNotifications = nextValue;
      resource.suppressAuditNotificationsExpiresAt = body.suppressAuditNotificationsExpiresAt;
      var ridKey = resource['@rid'] ? resource['@rid'].toString() : null;
      applyNormalizedSuppression(resource);
      applySuppressionSnapshot(ridKey, {
        suppressAuditNotifications: nextValue,
        disableAdminAuditNotifications: nextValue,
        disableReceiverAuditNotifications: nextValue,
        suppressAuditNotificationsExpiresAt: normalizeMuteExpiration(body.suppressAuditNotificationsExpiresAt)
      });
      var suppressedList = ctrl.suppressedResources();
      if (Array.isArray(suppressedList) && ridKey) {
        if (nextValue === 1) {
          var exists = suppressedList.some(function (item) {
            return item && item['@rid'] && item['@rid'].toString() === ridKey;
          });
          if (!exists) {
            ctrl.suppressedResources(sortResourcesByName(suppressedList.concat([resource])));
          }
        } else {
          var remaining = suppressedList.filter(function (item) {
            return !(item && item['@rid'] && item['@rid'].toString() === ridKey);
          });
          if (remaining.length !== suppressedList.length) {
            ctrl.suppressedResources(remaining);
          }
        }
      }
      var suppressedIds = ctrl.suppressedResourceIds();
      if (Array.isArray(suppressedIds) && ridKey) {
        if (nextValue === 1) {
          if (suppressedIds.indexOf(ridKey) === -1) {
            ctrl.suppressedResourceIds(suppressedIds.concat([ridKey]));
          }
        } else {
          var idsRemaining = suppressedIds.filter(function (id) { return id !== ridKey; });
          if (idsRemaining.length !== suppressedIds.length) {
            ctrl.suppressedResourceIds(idsRemaining);
          }
        }
      }
    })
    .catch(function (error) {
      var message = (error && error.message) || (error && error.error) || 'Failed to update audit notification preference';
      alerts.vm.add({detail: message});
    })
    .then(function () {
      resource._savingSuppression = false;
      m.redraw();
    });
  };

  ctrl.openSuppressionEditor = function (resource) {
    if (!resource) return;
    var expiresAt = Number(resource.suppressAuditNotificationsExpiresAt);
    suppressionDurationModal.open({
      resource: resource,
      expiresAt: expiresAt,
      onSave: function (nextExpiry) {
        return ctrl.updateSuppression(resource, 1, nextExpiry);
      },
      onDisable: function () {
        return ctrl.updateSuppression(resource, 0, null);
      }
    });
  };

  ctrl.toggleNotificationSuppression = function (resource) {
    if (!resource || !resource['@rid'] || resource._savingSuppression) {
      return;
    }
    var isCurrentlySuppressed = ctrl.isSuppressed(resource);
    if (!isCurrentlySuppressed) {
      ctrl.openSuppressionEditor(resource);
      return;
    }
    ctrl.updateSuppression(resource, 0, null);
  }

  return Promise.all([
    ctrl.loadAuditResourceIds(),
    ctrl.fetchSuppressedResources()
  ]);
};

page.onupdate = function () {
  var ctrl = this;
  if (ctrl.loadingResourceIds && ctrl.loadingResourceIds()) return;
  if (ctrl.loadResourcePage) ctrl.loadResourcePage();
};

page.onremove = function () {
  var ctrl = this;
  if (ctrl.clearResourceSkeletonTimer) ctrl.clearResourceSkeletonTimer();
};

page.view = function () {
  var ctrl = this;
  var resourceCount = ctrl.filteredResourceCount() || ctrl.points().length;
  var visiblePageKey = auditResourceIds(ctrl.pointsToShow).join('|');
  var visiblePageSize = auditResourceIds(ctrl.pointsToShow).length || AUDIT_RESOURCE_PAGE_SIZE;
  var loadingVisiblePage = (ctrl.loadingResources() && ctrl.showResourceSkeleton()) || visiblePageKey !== ctrl.currentResourcePageKey;
  return m('div.container.overflow-hidden', [
    m(heading, {label: 'Offline Resources', class: 'f-subheadline mb3 pb1 display'},
      ctrl.loadingResourceIds() ? m('span.f4.mid-gray', 'Loading') : resourceCount
    ),
    ctrl.loadingResourceIds() ? auditResourcesSkeletonTable(ctrl, AUDIT_RESOURCE_PAGE_SIZE) : m(paginationWithSearch, {
      data: ctrl.points(),
      newNode: ctrl.newNode,
      dataToShow: ctrl.pointsToShow,
      hideSearch: true,
      itemsPerPage: AUDIT_RESOURCE_PAGE_SIZE,
      filteredDataLength: ctrl.filteredResourceCount,
      onPageChange: function () {
        ctrl.loadResourcePage();
      }
    }, [
      loadingVisiblePage ? auditResourcesSkeletonTable(ctrl, visiblePageSize) : auditResourcesTable(ctrl)
    ]),
    (function () {
      var suppressedExpanded = ctrl.suppressedSectionOpen();
      var suppressedContainerClass = `div.cf.bw1.mv2.b--gray${suppressedExpanded ? '.pv3.bt.pl2.bl' : '.pa3.ba'}`;
      return m(suppressedContainerClass, {
        style: {
          'border-left-style': suppressedExpanded ? 'dotted' : 'solid'
        }
      }, [
        m('div.v-btm.flex.justify-between.items-center.flex-wrap.f3.pointer', {
          onclick: function () {
            if (ctrl.suppressedResources().length > 0) {
              ctrl.suppressedSectionOpen(!suppressedExpanded);
            }
          }
        }, [
          m('div.flex.items-center', [
            m('div.yellow.mr3', 'Muted Resources'),
            m('div.br2.nowrap', { style: { 'background-color': '#262626' } }, [
              m('span.f4.ph2.yellow', ctrl.suppressedResources().length)
            ])
          ]),
          ctrl.suppressedResources().length > 0 ? m(`i.icon.fr.blue${suppressedExpanded ? '.ion-close.f3' : '.ion-chevron-down.f3'}`) : '',
        ]),
        suppressedExpanded ? (function () {
          if (ctrl.loadingSuppressed()) {
            return m('div.mid-gray.mt3', 'Loading muted resources...');
          }
          if (!ctrl.suppressedResources().length) {
            return m('div.mid-gray.mt3', 'No muted resources found.');
          }
          return m('div.mt3.dragscroll-container.audit-table-scroll', [
            m('div.dragscroll', {oncreate: dragscroll}, [
              m('table.w-100.f4.mb5', [
                m('thead', [
                  m('tr', [
                    m('th.ph0.tl', 'Name'),
                    m('th.ph0.tl', 'Alarm Monitoring'),
                    m('th.ph0.tl', 'Mute')
                  ])
                ]),
                m('tbody', [
                  ctrl.suppressedResources().map(function (resourceRecord) {
                  return m('tr', [
                    m('td.tl',
                      m('a.red', {
                        href: '/#/' + (resourceRecord['@id'].split('/').slice(3).join('/').toLowerCase())
                      }, resourceBreadcrumbLabel(resourceRecord))
                    ),
                    m('td.tl', Array.isArray(resourceRecord.children) && resourceRecord.children.filter(function (p) { return Boolean(Number(p.alarmEnabled)); }).length ? '✅' : ''),
                    m('td.tl',
                      suppressionToggleButton(resourceRecord, ctrl.isSuppressed(resourceRecord), resourceRecord._savingSuppression, function () { ctrl.toggleNotificationSuppression(resourceRecord); })
                    )
                  ]);
                  })
                ])
              ])
            ])
          ]);
        })() : null
      ]);
    })()
  ]);
}

page.__test = {
  auditResourcesTable: auditResourcesTable,
  auditResourceSortParams: auditResourceSortParams
};

module.exports = page;
